import React from "react";
import { useTranslation } from "react-i18next";
import {
  User, Phone, MapPin, Store, Package, Hash, Boxes, MessageSquare, CheckCircle2, Circle,
} from "lucide-react";
import { Drawer } from "../../../components/ui/Drawer";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { packageStatus } from "../../../utils/packageStatus";
import { orderTypeLabel } from "../../../utils/orderTypeLabel";
import { recipientName, comercioName, comunaName, addressLine, STATUS_RECOLECTADO } from "./mockData";

const hhmm = (ts) => {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{title}</h3>
      <div className="rounded-xl border border-border divide-y divide-border">{children}</div>
    </section>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <Icon className="h-4 w-4 text-fg-subtle mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-fg-muted">{label}</p>
        <p className={`text-sm text-fg ${mono ? "font-mono tabular-nums" : ""}`}>{value || "—"}</p>
      </div>
    </div>
  );
}

export default function OrderDetailDrawer({ open, onClose, order, recibidoAt, bodegaName }) {
  const { t } = useTranslation();
  if (!order) return null;

  const recolectado = order.status === STATUS_RECOLECTADO;
  const dim = order.packageDimensions || {};

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`#${order.followingNumber}`}
      description={comercioName(order)}
      size="md"
      footer={<Button variant="outline" onClick={onClose}>{t("Cerrar")}</Button>}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <Badge tone={recolectado ? "success" : "neutral"} dot>{t(packageStatus[order.status] || "—")}</Badge>
          <Badge tone="neutral">{orderTypeLabel(order.type)}</Badge>
        </div>

        {/* línea de tiempo simple pendiente -> recolectado */}
        <Section title={t("Estado de recepción")}>
          <div className="flex items-center gap-3 px-4 py-3">
            <CheckCircle2 className="h-5 w-5 text-status-success" />
            <div className="flex-1">
              <p className="text-sm font-medium text-fg">{t("Pendiente")}</p>
              <p className="text-xs text-fg-muted">{t("Creado por el comercio")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            {recolectado ? <CheckCircle2 className="h-5 w-5 text-status-success" /> : <Circle className="h-5 w-5 text-fg-subtle" />}
            <div className="flex-1">
              <p className="text-sm font-medium text-fg">{t("Recolectado en bodega")}</p>
              <p className="text-xs text-fg-muted">
                {recolectado
                  ? `${bodegaName}${recibidoAt ? ` · ${hhmm(recibidoAt)}` : ""}`
                  : t("Pendiente de recepción")}
              </p>
            </div>
          </div>
        </Section>

        <Section title={t("Destinatario")}>
          <Row icon={User} label={t("Nombre")} value={recipientName(order)} />
          <Row icon={Phone} label={t("Teléfono")} value={order.receiver_phone} mono />
          <Row icon={MapPin} label={t("Dirección")} value={addressLine(order)} />
          <Row icon={MapPin} label={t("Comuna")} value={comunaName(order)} />
        </Section>

        <Section title={t("Comercio (remitente)")}>
          <Row icon={Store} label={t("Nombre comercial")} value={comercioName(order)} />
          <Row icon={MapPin} label={t("Dirección de origen")} value={order.senderInfo?.senderAddress} />
        </Section>

        <Section title={t("Detalles del paquete")}>
          <Row icon={Hash} label={t("N° de venta")} value={order.order_id} mono />
          <Row icon={Boxes} label={t("Dimensiones (alto×ancho×largo)")} value={`${dim.height || "—"} × ${dim.width || "—"} × ${dim.length || "—"} cm`} />
          <Row icon={Package} label={t("Peso")} value={dim.weight ? `${dim.weight} kg` : "—"} />
          {order.comments ? <Row icon={MessageSquare} label={t("Comentarios")} value={order.comments} /> : null}
        </Section>
      </div>
    </Drawer>
  );
}
